import type { AddCaseTemplateFormValues } from "@/components/Forms/SubForms/AddEditCaseTemplateDialog.tsx";
import { caseTemplateSchema } from "@/components/Forms/schema/caseTemplateSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector, handleJsonApiError } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "template",
    attributesSchema: caseTemplateSchema,
});

export const templateSelector = createDataSelector(resourceSelector);
export type NewTemplate = ReturnType<typeof templateSelector>;

export const useAddCaseTemplate = (
    caseTemplateUUID?: string,
): UseMutationResult<AddCaseTemplateFormValues, Error, AddCaseTemplateFormValues> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (attributes) => {
            const body = {
                caseTemplateUUID: caseTemplateUUID,
                ...attributes,
            };

            const response = await fetch(apiUrl("/case-template/add-edit"), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify(body),
            });
            await handleJsonApiError(response);
            return await response.json();
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["case-templates"],
            });
        },
    });
};
